<template>
	<Row :gutter="[16, 8]">
		<Col :span="18" :offset="0">
			<VerticalLayout>
				<StatsChartTitleWithDescription
					titleKey="stats.convergence.chartTitle"
					descriptionKey="stats.convergence.chartTitleTooltip"
					titleClass="pd-lg-x pd-md-top"
				/>
				<TimeSeriesChart :isLoading="isLoading" :series="series" :options="chartOptions" :height="500" />
				<ConvergenceCountChart
					titleKey="stats.convergence.baseStationsCountChartTitle"
					:mountpoints="gsCountMountpoints"
					:apiData="countsApiData"
					:isLoading="countsIsLoading"
					:datesRangeSafe="datesRangeSafe"
					:subNetworks="subNetworks"
				/>
				<ConvergenceCountChart
					titleKey="stats.convergence.satellitesCountChartTitle"
					:mountpoints="ssCountMountpoints"
					:apiData="countsApiData"
					:isLoading="countsIsLoading"
					:datesRangeSafe="datesRangeSafe"
					:subNetworks="subNetworks"
				/>
			</VerticalLayout>
		</Col>
		<Col :span="6" :offset="0">
			<VerticalLayout>
				<Card :bordered="false">
					<VerticalLayout>
						<SubNetworkSelect
							ref="subNetworkSelectRef"
							:allowClear="true"
							:onChange="fetchStatsItems"
						/>
						<DateRangeChooser v-model="datesRange" @change="fetchStatsItems" />
						<VerticalLayout v-if="isAggregated" class="pd-vertical">
							<LabeledSwitch v-model="showMin" :rightLabel="$t('controls.switches.showMin')" />
							<LabeledSwitch v-model="showMax" :rightLabel="$t('controls.switches.showMax')" />
						</VerticalLayout>
						<GnssSelect
							ref="gnssSelectRef"
							labelKey="stats.convergence.gnssSelectLabel"
							:codes="convergenceGnssCodes"
							:defaultSelected="convergenceGnssCodes"
							:onChange="fetchStatsItems"
						/>
					</VerticalLayout>
				</Card>
			</VerticalLayout>
		</Col>
	</Row>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue';
import { Row, Col, Card } from 'ant-design-vue';

import Api from '@/api/Api';

import { useI18n } from 'vue-i18n';

import { useSessionStore } from '@/stores/session';
import { useDateRangeAdapter } from '@/utils/useDateRangeAdapter';
import { useFrozenSubNetworks } from '@/components/common/useFrozenSubNetworks';

import { getBaseOptions, generateSeries, setXAxisRange, nullSafeFormatter, makeSubNetworkParamSeriesKey, getSubNetworkLabel, colorWithOpacity } from '@/utils/chartsUtils';

import VerticalLayout from '@/components/common/VerticalLayout';
import DateRangeChooser from '@/components/common/DateRangeChooser';
import TimeSeriesChart from '@/components/stats/TimeSeriesChart';
import StatsChartTitleWithDescription from '@/components/stats/StatsChartTitleWithDescription.vue';
import LabeledSwitch from '@/components/common/LabeledSwitch.vue';
import SubNetworkSelect from '@/components/common/controls/SubNetworkSelect.vue';
import GnssSelect from '@/components/common/controls/GnssSelect.vue';
import ConvergenceCountChart from '@/components/stats/ConvergenceCountChart.vue';


const GS_COUNT_MOUNTPOINTS = ['gs_cnt_total', 'gs_cnt_isln'];
const SS_COUNT_MOUNTPOINTS = ['ss2cnt_total', 'ss2cnt_isln'];
const CONVERGENCE_GNSS_CODES = ['G', 'E', 'C'];
const CONVERGENCE_THRESHOLD_FIELDS = {
	baseStations: 'conv_base_stations_threshold',
	satellites: 'conv_satellites_threshold',
};
const THRESHOLD_LINE_OPACITY = 0.7;
const THRESHOLD_LINE_WIDTH = 1;

const getValue = item => item.final_value / 10_000;

const buildConvergenceThresholdAnnotations = ({
	statsItems,
	convergenceThresholds,
	series,
	chartColors,
	subNetworks,
	getMountpointLabel,
}) => {
	const seen = new Set();
	const lines = [];
	// уникальные (подсеть, метрика) из фактических данных графика
	const pairs = new Map();
	for (const item of statsItems) {
		if (item.kind !== 'convergence') {
			continue;
		}
		const pairKey = item.sub_network + '|' + item.mountpoint;
		pairs.set(pairKey, { subNetworkId: item.sub_network, mountpoint: item.mountpoint });
	}
	for (const { subNetworkId, mountpoint } of pairs.values()) {
		const thresholdField = CONVERGENCE_THRESHOLD_FIELDS[mountpoint];
		if (thresholdField == null) {
			continue;
		}
		const thresholds = convergenceThresholds[subNetworkId];
		if (thresholds == null) {
			continue;
		}
		const y = thresholds[thresholdField];
		if (y == null) {
			continue;
		}
		// цвет линии = цвет соответствующей серии
		const baseKey = getSubNetworkLabel(subNetworks, subNetworkId) + '-' + getMountpointLabel(mountpoint);
		const seriesIndex = series.findIndex(s => s.name === baseKey || s.name.startsWith(baseKey + '-'));
		if (seriesIndex < 0) {
			continue;
		}
		const color = chartColors[seriesIndex];
		if (color == null) {
			continue;
		}
		const borderColor = colorWithOpacity(color, THRESHOLD_LINE_OPACITY);
		// не дублировать одинаковые (y, цвет)
		const dedupeKey = y + '|' + borderColor;
		if (seen.has(dedupeKey)) {
			continue;
		}
		seen.add(dedupeKey);
		lines.push({
			y,
			borderColor,
			borderWidth: THRESHOLD_LINE_WIDTH,
			strokeDashArray: 4,
		});
	}
	return lines;
};

export default {
	components: {
		Row, Col, Card,
		VerticalLayout, DateRangeChooser, TimeSeriesChart, StatsChartTitleWithDescription, LabeledSwitch,
		SubNetworkSelect, GnssSelect, ConvergenceCountChart,
	},
	setup() {
		const { t } = useI18n();
		const sessionStore = useSessionStore();
		const { datesRange, datesRangeStr, datesRangeSafe } = useDateRangeAdapter(sessionStore, 1, 0);
		const showMin = ref(true);
		const showMax = ref(true);
		const subNetworkSelectRef = ref();
		const gnssSelectRef = ref();

		const { apiData, isLoading, doRequest } = Api.useConvergenceStatsAggregatedGet();
		const {
			apiData: countsApiData,
			isLoading: countsIsLoading,
			doRequest: doCountsRequest,
		} = Api.useConvergenceCountsStatsAggregatedGet();
		const isAggregated = computed(() => apiData.value.period > 1 || countsApiData.value.period > 1);

		const { subNetworks } = useFrozenSubNetworks();

		const getSubNetworkIdForRequest = () => {
			if (subNetworkSelectRef.value == null || subNetworkSelectRef.value.selectedId == null) {
				return null;
			}
			return subNetworkSelectRef.value.selectedId;
		};

		const fetchStatsItems = () => {
			if (datesRangeStr.value != null) {
				const requestData = {
					start_date: datesRangeStr.value[0],
					end_date: datesRangeStr.value[1],
					sub_network_id: getSubNetworkIdForRequest(),
					show_min: showMin.value,
					show_max: showMax.value,
				};
				doRequest(requestData);
				const gnssSelectedValue = gnssSelectRef.value != null ? gnssSelectRef.value.selected : [];
				doCountsRequest({
					...requestData,
					gnss: gnssSelectedValue,
				});
			}
		};

		watch([showMin, showMax], () => {
			fetchStatsItems();
		});

		onMounted(() => {
			fetchStatsItems();
		});

		const series = computed(() => generateSeries({
			items: apiData.value.statsItems.filter(item => item.kind === 'convergence'),
			getSeriesKeyFn: makeSubNetworkParamSeriesKey({
				subNetworks: subNetworks.value,
				period: apiData.value.period,
				getParamFn: item => t('stats.convergence.' + item.mountpoint),
			}),
			getValueFn: getValue,
			seriesStep: apiData.value.period,
			discontinuous: true,
		}).series);

		const chartOptions = computed(() => {
			let options = getBaseOptions('convergenceAggregated', series.value, apiData.value.funcCount);
			options.yaxis.title.text = '%';
			options.yaxis.min = 0;
			options.yaxis.decimalsInFloat = 1;
			setXAxisRange(options, datesRangeSafe.value);
			options.tooltip.y.formatter = nullSafeFormatter(value => value.toFixed(1) + ' %');
			const thresholdLines = buildConvergenceThresholdAnnotations({
				statsItems: apiData.value.statsItems,
				convergenceThresholds: apiData.value.convergenceThresholds,
				series: series.value,
				chartColors: options.colors,
				subNetworks: subNetworks.value,
				getMountpointLabel: mountpoint => t('stats.convergence.' + mountpoint),
			});
			if (thresholdLines.length > 0) {
				options.annotations = { yaxis: thresholdLines };
			}
			return options;
		});

		return {
			isLoading,
			countsApiData,
			countsIsLoading,
			datesRange,
			datesRangeSafe,
			showMin,
			showMax,
			isAggregated,
			series,
			chartOptions,
			subNetworks,
			subNetworkSelectRef,
			gnssSelectRef,
			gsCountMountpoints: GS_COUNT_MOUNTPOINTS,
			ssCountMountpoints: SS_COUNT_MOUNTPOINTS,
			convergenceGnssCodes: CONVERGENCE_GNSS_CODES,
			fetchStatsItems,
		};
	},
};
</script>
